import React from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSlotLockDetails, getSlotLockHistory, getSlotSummary, lockSlot, unlockSlot } from '../api/analyticsApi';
import { queryKeys } from '../api/queryKeys';
import { isFeatureEnabled } from '../utils/featureFlags';
import { dateInputValue } from '../utils/date';

const NEW_SLOT_EFFECTIVE_DATE = '2026-06-08';
const BRANCHES = ['Adda Plot', 'Gulberg'];
const BRANCH_COLORS = {
  'Adda Plot': 'var(--blue)',
  Gulberg: 'var(--green)'
};

const NEW_SLOT_GROUPS = [
  { label: 'Hi Tea / Lunch', slots: ['Slot 02: 12:15 PM - 1:45 PM', 'Slot 03: 2:00 PM - 3:30 PM', 'Slot 04: 3:45 PM - 5:15 PM', 'Slot 05: 5:30 PM - 7:00 PM'] },
  { label: 'Dinner Slots', slots: ['Slot 06: 7:15 PM - 8:45 PM', 'Slot 07: 9:00 PM - 11:00 PM'] }
];

const OLD_SLOT_GROUPS = [
  { label: 'Breakfast (Nashta)', slots: ['Slot 1(A): 10:00AM - 11:30AM', 'Slot 1(B): 11:45AM - 1:15PM'] },
  { label: 'Lunch/Hi-Tea', slots: ['Slot 2: 1:30PM - 3:00PM', 'Slot 3: 3:15PM - 4:45PM', 'Slot 4: 5:00PM - 6:30PM'] },
  { label: 'Dinner', slots: ['Slot 5: 6:45PM - 8:15PM', 'Slot 6: 8:30PM - 10:00PM', 'Slot 7: 10:15PM - 12:00AM'] }
];

const SLOT_LABEL_ALIASES = {
  '10:00 AM - 12:00 PM': 'Slot 01: 10:00 AM - 12:00 PM',
  '12:15 PM - 1:45 PM': 'Slot 02: 12:15 PM - 1:45 PM',
  '2:00 PM - 3:30 PM': 'Slot 03: 2:00 PM - 3:30 PM',
  '3:45 PM - 5:15 PM': 'Slot 04: 3:45 PM - 5:15 PM',
  '5:30 PM - 7:00 PM': 'Slot 05: 5:30 PM - 7:00 PM',
  '7:15 PM - 8:45 PM': 'Slot 06: 7:15 PM - 8:45 PM',
  '9:00 PM - 11:00 PM': 'Slot 07: 9:00 PM - 11:00 PM',
  '10:00AM - 11:30AM': 'Slot 1(A): 10:00AM - 11:30AM',
  '11:45AM - 1:15PM': 'Slot 1(B): 11:45AM - 1:15PM',
  '1:30PM - 3:00PM': 'Slot 2: 1:30PM - 3:00PM',
  '3:15PM - 4:45PM': 'Slot 3: 3:15PM - 4:45PM',
  '5:00PM - 6:30PM': 'Slot 4: 5:00PM - 6:30PM',
  '6:45PM - 8:15PM': 'Slot 5: 6:45PM - 8:15PM',
  '8:30PM - 10:00PM': 'Slot 6: 8:30PM - 10:00PM',
  '10:15PM - 12:00AM': 'Slot 7: 10:15PM - 12:00AM'
};

const ADVANCE_DETAILS = [
  ['paidAttached', 'Adv + screenshot', 'var(--green)'],
  ['paidMissing', 'Adv no screenshot', '#f5a623'],
  ['payOnArrival', 'Pay on Arrival', '#f97316'],
  ['waived', 'Advance Waived', '#9b5cff'],
  ['pending', 'Pending', 'var(--text3)']
];

function visibleBranches() {
  try {
    const branches = window.visibleBranchNames?.() || [];
    return Array.isArray(branches) && branches.length ? branches : BRANCHES;
  } catch (err) {
    return BRANCHES;
  }
}

function todayKey() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Karachi',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).formatToParts(new Date());
  const value = type => parts.find(part => part.type === type)?.value || '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function relativeDateKey(daysDelta = 0) {
  const date = new Date();
  date.setDate(date.getDate() + daysDelta);
  return dateInputValue(date);
}

function isManagementUser() {
  try {
    const storedUser = JSON.parse(window.localStorage.getItem('user') || '{}');
    const user = { ...storedUser, ...(window.activeUserContext?.() || {}) };
    const role = String(user.role || user.Role || '').trim().toLowerCase();
    const team = String(user.Team || user.team || '').trim().toLowerCase();
    return window.isManagementTeam?.() === true || team === 'management' || role === 'superadmin';
  } catch (err) {
    return false;
  }
}

function metric(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function dateLabel(value) {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function slotGroupsForDate(dateValue = '') {
  if (typeof window.nexusSlotGroupsForDate === 'function') return window.nexusSlotGroupsForDate(dateValue);
  const key = String(dateValue || '').slice(0, 10);
  return !key || key >= NEW_SLOT_EFFECTIVE_DATE ? NEW_SLOT_GROUPS : OLD_SLOT_GROUPS;
}

function slotsForDate(dateValue = '') {
  return slotGroupsForDate(dateValue).flatMap(group => group.slots);
}

function slotLabel(slot = '') {
  const value = String(slot || '').trim();
  return SLOT_LABEL_ALIASES[value] || window.slotDisplayLabel?.(value) || value || '-';
}

function slotComparable(slot = '') {
  return slotLabel(slot).toLowerCase().replace(/\s+/g, ' ').trim();
}

function slotShortLabel(slot = '') {
  const match = slotLabel(slot).match(/Slot\s+0?(\d+)/i);
  return match ? `S${String(match[1]).padStart(2, '0')}` : slotLabel(slot).slice(0, 5);
}

function slotTimeLabel(slot = '') {
  return slotLabel(slot).replace(/^Slot\s+0?\d+(?:\([A-Z]\))?\s*:\s*/i, '').trim();
}

function parseClockMinutes(value = '') {
  const match = String(value).trim().match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
  if (!match) return 0;
  let hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return (hours * 60) + minutes;
}

function parseSlotWindow(slot) {
  const clean = slotTimeLabel(slot);
  const [startText, endText] = clean.split(/\s+-\s+/);
  const start = parseClockMinutes(startText);
  let end = parseClockMinutes(endText);
  if (end <= start) end += 24 * 60;
  return { type: 'slot', slot, start, end, minutes: end - start, label: clean };
}

function formatAxisTime(minutes) {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hours24 = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${meridiem}`;
}

function pkDateTimeLabel(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Karachi',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date).replace(',', '');
}

function segmentPixels(minutes) {
  return minutes <= 20 ? 28 : 180;
}

function buildTimelineSegments(date) {
  const windows = slotsForDate(date).map(parseSlotWindow).sort((a, b) => a.start - b.start);
  return windows.flatMap((slot, index) => {
    const segments = [{ ...slot, pixelWidth: segmentPixels(slot.minutes), width: `${segmentPixels(slot.minutes)}px` }];
    const next = windows[index + 1];
    if (next && next.start > slot.end) {
      const minutes = next.start - slot.end;
      segments.push({ type: 'gap', start: slot.end, end: next.start, minutes, pixelWidth: segmentPixels(minutes), width: `${segmentPixels(minutes)}px` });
    }
    return segments;
  });
}

function emptyAdvance() {
  return {
    paidAttached: { count: 0, pax: 0 },
    paidMissing: { count: 0, pax: 0 },
    payOnArrival: { count: 0, pax: 0 },
    waived: { count: 0, pax: 0 },
    pending: { count: 0, pax: 0 }
  };
}

function normalizeItem(item = {}) {
  return {
    key: item.key || '',
    count: Number(item.count || 0),
    pax: Number(item.pax || 0),
    billing: Number(item.billing || item.actual || 0),
    advance: { ...emptyAdvance(), ...(item.advance || {}) },
    newGuests: Number(item.newGuests || 0),
    newPax: Number(item.newPax || 0),
    returningGuests: Number(item.returningGuests || 0),
    returningPax: Number(item.returningPax || 0)
  };
}

function itemByKey(entries = []) {
  const map = new Map();
  entries.forEach(item => map.set(String(item.key || '').toLowerCase(), normalizeItem(item)));
  return map;
}

function branchSlotRows(data, branch, date) {
  const lockMap = new Map((data?.slotLocks || []).map(lock => [`${String(lock.branch || '').toLowerCase()}::${slotComparable(lock.slot || '')}`, lock]));
  const branchSlotMap = itemByKey(data?.slotBranchEntries || []);
  return slotsForDate(date).map(slot => {
    const key = `${branch} · ${slotLabel(slot)}`.toLowerCase();
    const found = branchSlotMap.get(key) || branchSlotMap.get(`${branch} · ${slot}`.toLowerCase());
    const slotLock = lockMap.get(`${String(branch || '').toLowerCase()}::${slotComparable(slot)}`) || null;
    return found ? { ...found, branch, slot, label: `${branch} · ${slotLabel(slot)}`, slotLock } : {
      branch,
      slot,
      label: `${branch} · ${slotLabel(slot)}`,
      count: 0,
      pax: 0,
      billing: 0,
      newGuests: 0,
      newPax: 0,
      returningGuests: 0,
      returningPax: 0,
      advance: emptyAdvance(),
      slotLock
    };
  });
}

function confirmedPax(item) {
  return ['paidAttached', 'paidMissing', 'payOnArrival', 'waived'].reduce((sum, key) => sum + Number(item.advance?.[key]?.pax || 0), 0);
}

function slotTypeLabel(slot = '') {
  const label = slotLabel(slot).toLowerCase();
  if (label.includes('10:00')) return 'Breakfast';
  if (label.includes('12:15') || label.includes('1:30') || label.includes('2:00') || label.includes('3:') || label.includes('5:')) return 'Hi-Tea / Lunch';
  return 'Dinner';
}

function currentKarachiMinutes() {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Karachi', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
  return (Number(parts.find(part => part.type === 'hour')?.value || 0) * 60) + Number(parts.find(part => part.type === 'minute')?.value || 0);
}

function canManageFutureSlot(date, slot) {
  const today = todayKey();
  if (date < today) return false;
  if (date > today) return true;
  return parseSlotWindow(slot).start > currentKarachiMinutes();
}

function activeLineLeft(segments, date) {
  if (date !== todayKey() || !segments.length) return null;
  const now = currentKarachiMinutes();
  const first = segments[0].start;
  const last = segments[segments.length - 1].end;
  if (now < first || now > last) return null;
  let offset = 0;
  for (const segment of segments) {
    if (now >= segment.end) {
      offset += segment.pixelWidth;
      continue;
    }
    if (now >= segment.start && now <= segment.end) {
      offset += segment.pixelWidth * ((now - segment.start) / Math.max(1, segment.end - segment.start));
      break;
    }
  }
  return { left: 112 + offset, label: formatAxisTime(now) };
}

function previousWeek(date) {
  const current = new Date(`${date}T00:00:00`);
  current.setDate(current.getDate() - 7);
  return dateInputValue(current);
}

function buildParams(date, branch) {
  const params = { date };
  if (branch) params.branch = branch;
  else {
    const branches = visibleBranches();
    if (branches.length) params.branches = branches.join(',');
  }
  return params;
}

function InsightCard({ label, value, sub, color }) {
  return (
    <div className="slot-insight-card" style={{ '--accent-color': color }}>
      <div className="slot-insight-label">{label}</div>
      <div className="slot-insight-value">{value}</div>
      <div className="slot-insight-sub">{sub}</div>
    </div>
  );
}

function BarRow({ label, value, pct, children, color }) {
  return (
    <div className="slot-session-bar-row">
      <div className="slot-session-bar-head"><span>{label}</span><span>{value}</span></div>
      <div className="slot-session-bar">
        {children || <div className="slot-session-bar-fill" style={{ width: `${pct}%`, background: color }} />}
      </div>
    </div>
  );
}

function ConfirmedSegments({ item, max }) {
  const total = confirmedPax(item);
  const width = Math.max(0, Math.min(100, (total / Math.max(1, max)) * 100));
  if (!total) return <div className="slot-session-confirmed" style={{ width: 0 }} />;
  return (
    <div className="slot-session-confirmed" style={{ width: `${width}%` }}>
      {ADVANCE_DETAILS.filter(([key]) => key !== 'pending').map(([key, label, color]) => {
        const pax = Number(item.advance?.[key]?.pax || 0);
        if (!pax) return null;
        return <span key={key} title={`${label}: ${metric(pax)} PAX`} style={{ width: `${Math.max(4, (pax / total) * 100)}%`, background: color }} />;
      })}
    </div>
  );
}

function AdvanceChips({ item }) {
  const chips = ADVANCE_DETAILS.map(([key, label, color]) => ({ key, label, color, data: item.advance?.[key] || { count: 0, pax: 0 } })).filter(entry => entry.data.count || entry.data.pax);
  if (!chips.length) return null;
  return <div className="slot-advance-chips">{chips.map(entry => <span key={entry.key} className="slot-advance-chip" style={{ color: entry.color, borderColor: entry.color }}>{entry.label}: {metric(entry.data.pax)}</span>)}</div>;
}

function SlotBlock({ item, segment, branchColor, maxPax, maxReservations, canManageLocks, onManageLock }) {
  const projectedPct = Math.max(0, Math.min(100, (item.pax / Math.max(1, maxPax)) * 100));
  const billingPct = Math.max(0, Math.min(100, (item.billing / Math.max(1, maxPax)) * 100));
  const confirmed = confirmedPax(item);
  const pending = Math.max(0, item.pax - confirmed);
  const bookingAlpha = Math.max(0.04, Math.min(0.34, (item.count / Math.max(1, maxReservations)) * 0.34));
  const tooltip = `${item.label}\nReservations: ${item.count}\nReservation PAX: ${metric(item.pax)}\nConfirmed PAX: ${metric(confirmed)}\nPending PAX: ${metric(pending)}\nBilling PAX: ${metric(item.billing)}`;
  return (
    <div className={`slot-session-block ${item.count ? 'has-bookings' : ''}`} title={tooltip} style={{ '--branch-color': branchColor, '--segment-width': segment.width, '--booking-alpha': bookingAlpha }}>
      <div className="slot-session-head">
        <div><div className="slot-session-slot">{slotShortLabel(item.slot)}</div><div className="slot-session-res">{slotTimeLabel(item.slot)}</div></div>
        <div className="slot-session-head-actions">
          <div className="slot-session-res">{item.count} res</div>
          {canManageLocks ? (
            <button
              className={`slot-lock-action ${item.slotLock?.isLocked ? 'is-locked' : ''}`}
              type="button"
              title={item.slotLock?.isLocked ? 'View lock details or unlock this slot' : 'Lock this slot'}
              onClick={() => onManageLock?.(item)}
            >
              {item.slotLock?.isLocked ? 'Unlock' : 'Lock'}
            </button>
          ) : item.slotLock?.isLocked ? <span className="slot-lock-status">Locked</span> : null}
        </div>
      </div>
      <div className="slot-session-body">
        {item.count ? (
          <>
            <BarRow label="Projected" value={`${metric(item.pax)} PAX`} pct={projectedPct} color="color-mix(in srgb, var(--branch-color) 72%, white)" />
            <BarRow label="Confirmed" value={`${metric(confirmed)} PAX`}><ConfirmedSegments item={item} max={maxPax} /></BarRow>
            <BarRow label="Billing" value={`${metric(item.billing)} PAX`} pct={billingPct} color="var(--green)" />
          </>
        ) : <div className="slot-session-empty">Empty</div>}
      </div>
      <div className="slot-session-foot">
        <strong>Reservation PAX: {metric(item.pax)}</strong>
        <span>New: {metric(item.newPax)} PAX · {item.newGuests || 0} res</span>
        <span>Returning: {metric(item.returningPax)} PAX · {item.returningGuests || 0} res</span>
        <span>Confirmed PAX: {metric(confirmed)}</span>
        <span>Pending PAX: {metric(pending)}</span>
        <span>Billing PAX: {metric(item.billing)}</span>
        <AdvanceChips item={item} />
        {item.slotLock?.isLocked ? <span>Locked by {item.slotLock.lockedBy || 'Management'} · {pkDateTimeLabel(item.slotLock.lockedAt)}</span> : null}
        {item.slotLock?.remarks ? <span>Remarks: {item.slotLock.remarks}</span> : null}
      </div>
    </div>
  );
}

function SlotTimeline({ branchRows, segments, date, canManageLocks, onManageLock }) {
  const allRows = branchRows.flatMap(group => group.rows);
  const maxPax = Math.max(1, ...allRows.flatMap(item => [item.pax, item.billing, confirmedPax(item)]));
  const maxReservations = Math.max(1, ...allRows.map(item => item.count));
  const line = activeLineLeft(segments, date);
  return (
    <div className="slot-session-chart">
      <div className="slot-session-scroll">
        <div className="slot-session-grid">
          {line ? <div className="slot-active-time-line" style={{ left: line.left }} title={`Current PKT time: ${line.label}`}><span>{line.label}</span></div> : null}
          <div className="slot-session-axis-row">
            <div className="slot-session-axis-spacer" />
            <div className="slot-session-axis">
              {segments.map((segment, index) => segment.type === 'gap'
                ? <div key={`gap-${index}`} className="slot-axis-gap" style={{ '--segment-width': segment.width }}>{segment.minutes}m</div>
                : <div key={segment.slot} className="slot-axis-segment" style={{ '--segment-width': segment.width }}><span>{formatAxisTime(segment.start)}</span><span>{formatAxisTime(segment.end)}</span></div>)}
            </div>
          </div>
          {branchRows.map(group => {
            const totals = group.rows.reduce((acc, item) => ({ count: acc.count + item.count, pax: acc.pax + item.pax, billing: acc.billing + item.billing }), { count: 0, pax: 0, billing: 0 });
            return (
              <div key={group.branch} className="slot-session-row" style={{ '--branch-color': group.color }}>
                <div className="slot-session-branch"><strong>{group.branch}</strong><span>{totals.count} res</span><span>{metric(totals.pax)} PAX</span></div>
                <div className="slot-session-timeline">
                  {segments.map((segment, index) => {
                    if (segment.type === 'gap') return <div key={`${group.branch}-gap-${index}`} className="slot-session-gap" style={{ '--segment-width': segment.width }} title={`${formatAxisTime(segment.start)} - ${formatAxisTime(segment.end)}`}>{segment.minutes}m</div>;
                    const item = group.rows.find(row => slotComparable(row.slot) === slotComparable(segment.slot)) || { branch: group.branch, slot: segment.slot, label: `${group.branch} · ${slotLabel(segment.slot)}`, count: 0, pax: 0, billing: 0, advance: emptyAdvance() };
                    return <SlotBlock key={`${group.branch}-${segment.slot}`} item={item} segment={segment} branchColor={group.color} maxPax={maxPax} maxReservations={maxReservations} canManageLocks={canManageLocks} onManageLock={onManageLock} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="slot-chart-legend">
        <span><i style={{ border: '2px solid var(--green)', background: 'rgba(45,212,160,.12)' }} />Gulberg</span>
        <span><i style={{ border: '2px solid var(--blue)', background: 'rgba(79,142,247,.12)' }} />Adda Plot</span>
        <span><i style={{ background: 'rgba(45,212,160,.22)' }} />Area intensity is reservation count</span>
        <span><i style={{ background: 'var(--blue)' }} />Projected bar</span>
        <span><i style={{ background: 'linear-gradient(90deg,var(--green),#f5a623,#f97316,#9b5cff)' }} />Confirmed bar</span>
        <span><i style={{ background: 'var(--green)' }} />Billing bar</span>
        <span><i style={{ border: '1px dashed var(--border2)', background: 'transparent' }} />Empty slot placeholder</span>
      </div>
    </div>
  );
}

function SlotLockModal({ item, date, onClose, onSaved }) {
  const [remarks, setRemarks] = React.useState('');
  React.useEffect(() => setRemarks(''), [date, item?.branch, item?.slot, item?.slotLock?.isLocked]);
  const detailQuery = useQuery({
    queryKey: ['slot-lock-details', date, item?.branch, item?.slot],
    queryFn: () => getSlotLockDetails({ date, branch: item?.branch, slot: item?.slot, includeLogs: 1, limit: 20 }),
    enabled: Boolean(item?.branch && item?.slot && date)
  });
  const mutation = useMutation({
    mutationFn: payload => (item?.slotLock?.isLocked ? unlockSlot(payload) : lockSlot(payload)),
    onSuccess: () => {
      onSaved?.();
      onClose?.();
    },
    onError: error => window.toast?.(error?.message || 'Slot management action failed', 'red')
  });
  if (!item) return null;
  const lock = detailQuery.data?.lock || item.slotLock || null;
  const logs = detailQuery.data?.logs || [];
  return createPortal(
    <div className="guest-modal slot-management-modal" style={{ zIndex: 9400 }}>
      <div className="guest-modal-card call-form-shell slot-management-modal-card slot-lock-modal-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ fontFamily: "'Gilroy-Bold','Gilroy-ExtraBold','Inter',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{lock?.isLocked ? 'Unlock Slot' : 'Lock Slot'}</h2>
            <p style={{ color: 'var(--text2)', margin: 0 }}>{item.branch} · {slotLabel(item.slot)} · {dateLabel(date)}</p>
          </div>
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
        {lock?.isLocked ? (
          <div className="call-empty" style={{ padding: 12, textAlign: 'left', marginBottom: 14 }}>
            <div><strong>Locked by:</strong> {lock.lockedBy || 'Management'}</div>
            <div><strong>Locked at:</strong> {pkDateTimeLabel(lock.lockedAt)}</div>
            <div><strong>Remarks:</strong> {lock.remarks || '—'}</div>
          </div>
        ) : null}
        <div className="call-form-section">
          <div className="call-form-section-title">{lock?.isLocked ? 'Unlock Remarks' : 'Lock Remarks'}</div>
          <div className="call-field full">
            <textarea rows="3" value={remarks} onChange={event => setRemarks(event.target.value)} placeholder={lock?.isLocked ? 'Reason for unlock' : 'Reason for locking this slot'} />
          </div>
        </div>
        <div className="call-form-section" style={{ marginTop: 16 }}>
          <div className="call-form-section-title">Recent Activity</div>
          {detailQuery.isLoading ? <div className="empty-state"><p>Loading lock history...</p></div> : null}
          {!detailQuery.isLoading && !logs.length ? <div className="call-empty" style={{ padding: 12 }}>No slot lock activity yet.</div> : null}
          {!detailQuery.isLoading && logs.length ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {logs.map(log => (
                <div key={log.id} className="call-empty" style={{ padding: 12, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <strong>{String(log.action || '').toLowerCase() === 'locked' ? 'Locked' : 'Unlocked'}</strong>
                    <span className="td-mono">{pkDateTimeLabel(log.performedAt)}</span>
                  </div>
                  <div className="kanban-mini" style={{ marginTop: 4 }}>By {log.performedBy || 'Unknown User'}</div>
                  {log.remarks ? <div className="kanban-mini" style={{ marginTop: 4 }}>{log.remarks}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Back</button>
          <button
            type="button"
            className={`btn-primary ${lock?.isLocked ? 'danger-btn' : ''}`}
            disabled={mutation.isPending || !remarks.trim()}
            title={!remarks.trim() ? 'Remarks are required' : ''}
            onClick={() => mutation.mutate({ date, branch: item.branch, slot: item.slot, remarks: remarks.trim() })}
          >
            {mutation.isPending ? 'Saving...' : (lock?.isLocked ? 'Unlock Slot' : 'Lock Slot')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BranchTables({ branchRows }) {
  return (
    <div className="slot-branch-tables">
      {branchRows.map(group => {
        const totals = group.rows.reduce((acc, item) => ({ count: acc.count + item.count, pax: acc.pax + item.pax, billing: acc.billing + item.billing }), { count: 0, pax: 0, billing: 0 });
        return (
          <div key={group.branch} className="slot-branch-table-card" style={{ '--branch-color': group.color }}>
            <div className="slot-branch-table-head"><strong>{group.branch}</strong><span>{totals.count} res - {metric(totals.pax)} reserved - {metric(totals.billing)} billing</span></div>
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table>
                <thead><tr><th>Slot</th><th>Reservations</th><th>Reserved</th><th>Billing</th></tr></thead>
                <tbody>{group.rows.map(item => <tr key={item.slot}><td>{slotLabel(item.slot)}</td><td className="td-mono">{item.count}</td><td className="td-mono">{metric(item.pax)}</td><td className="td-mono">{metric(item.billing)}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SlotManagement({ branchRows, date, canManageLocks, onManageLock }) {
  const rows = branchRows.flatMap(group => group.rows.map(item => ({ ...item, color: group.color })));
  return (
    <div className="slot-management-section">
      <div className="slot-management-head">
        <div>
          <h2>Slot Management</h2>
          <p>Lock or reopen reservation slots for {dateLabel(date)}.</p>
        </div>
        {!canManageLocks ? <span className="call-tag">View only</span> : null}
      </div>
      <div className="slot-management-grid">
        {rows.map(item => {
          const manageable = canManageFutureSlot(date, item.slot);
          return (
          <div key={`${item.branch}-${item.slot}`} className={`slot-management-item ${item.slotLock?.isLocked ? 'is-locked' : ''}`} style={{ '--branch-color': item.color }}>
            <div className="slot-management-copy">
              <div className="slot-management-title"><strong>{item.branch}</strong><span>{slotShortLabel(item.slot)}</span></div>
              <div className="slot-management-time">{slotTimeLabel(item.slot)}</div>
              <div className="slot-management-metrics">
                <span><small>Reservations</small><strong>{metric(item.count)}</strong></span>
                <span><small>Reserved</small><strong>{metric(item.pax)} PAX</strong></span>
                <span><small>Confirmed</small><strong>{metric(confirmedPax(item))} PAX</strong></span>
                <span><small>Pending</small><strong>{metric(Math.max(0, item.pax - confirmedPax(item)))} PAX</strong></span>
                <span><small>Billing</small><strong>{metric(item.billing)} PAX</strong></span>
              </div>
              <AdvanceChips item={item} />
              {item.slotLock?.isLocked ? (
                <div className="slot-management-meta">
                  <span>Locked by {item.slotLock.lockedBy || 'Management'}</span>
                  <span>{pkDateTimeLabel(item.slotLock.lockedAt)} PKT</span>
                  <span>{item.slotLock.remarks || 'No remarks provided'}</span>
                </div>
              ) : <div className="slot-management-meta"><span>Open for reservations</span></div>}
            </div>
            <div className="slot-management-actions">
              <span className={`slot-management-status ${item.slotLock?.isLocked ? 'is-locked' : ''}`}>{manageable ? (item.slotLock?.isLocked ? 'Locked' : 'Open') : 'Closed'}</span>
              {canManageLocks ? (
                <button type="button" disabled={!manageable} title={!manageable ? 'Only future slots can be managed' : ''} className={item.slotLock?.isLocked ? 'btn-secondary' : 'btn-primary'} onClick={() => onManageLock?.(item)}>
                  {item.slotLock?.isLocked ? 'Review / Unlock' : 'Lock Slot'}
                </button>
              ) : null}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

export function SlotSummaryPage() {
  const [date, setDate] = React.useState(todayKey());
  const [branch, setBranch] = React.useState('');
  const [lockTarget, setLockTarget] = React.useState(null);
  const queryClient = useQueryClient();
  const managementUser = isManagementUser();
  const visible = visibleBranches();
  const branches = branch ? [branch] : BRANCHES.filter(item => visible.includes(item));
  const params = React.useMemo(() => buildParams(date, branch), [date, branch]);
  const previousParams = React.useMemo(() => buildParams(previousWeek(date), branch), [date, branch]);
  const { data, isLoading, isFetching, error } = useQuery({ queryKey: queryKeys.slotSummary(params), queryFn: () => getSlotSummary(params), placeholderData: previous => previous });
  const { data: previousData } = useQuery({ queryKey: queryKeys.slotSummary(previousParams), queryFn: () => getSlotSummary(previousParams), placeholderData: previous => previous });

  const segments = React.useMemo(() => buildTimelineSegments(date), [date]);
  const branchRows = React.useMemo(() => branches.map(item => ({ branch: item, color: BRANCH_COLORS[item] || 'var(--accent)', rows: branchSlotRows(data, item, date) })), [branches.join('|'), data, date]);
  const topSlot = data?.slotEntries?.[0];
  const topBranch = data?.branchEntries?.[0];
  const sameWeekdayDiff = Number(data?.totals?.pax || 0) - Number(previousData?.totals?.pax || 0);
  const reservationDiff = Number(data?.totals?.count || 0) - Number(previousData?.totals?.count || 0);

  React.useEffect(() => {
    const legacy = document.getElementById('slot-summary-legacy');
    const legacyHeader = document.querySelector('#page-slotsummary > .page-header');
    if (legacy) legacy.hidden = true;
    if (legacyHeader) legacyHeader.hidden = true;
    const previousRenderer = window.renderSlotSummary;
    window.renderSlotSummary = () => window.NexusReact?.queryClient?.invalidateQueries?.({ queryKey: ['analytics', 'slot-summary'] });
    return () => {
      if (legacy) legacy.hidden = false;
      if (legacyHeader) legacyHeader.hidden = false;
      window.renderSlotSummary = previousRenderer;
    };
  }, []);

  React.useEffect(() => {
    if (!managementUser && date !== todayKey()) setDate(todayKey());
  }, [managementUser, date]);

  return (
    <>
      <div className="page-header">
        <h1>Slot Summary</h1>
        <p>Single-day slot movement with reservations, gaps, and billing PAX</p>
      </div>
      <div className="filters-bar">
        <div className="filter-group"><span className="filter-label">Branch</span><select value={branch} onChange={event => setBranch(event.target.value)}><option value="">All</option>{visible.map(item => <option key={item} value={item}>{item}</option>)}</select></div>
        <div className="filter-group"><span className="filter-label">Summary Date</span><input type="date" value={date} onChange={event => setDate(event.target.value || todayKey())} /></div>
      </div>
      <div className="chart-card slot-summary-react-card" style={{ marginBottom: 16, opacity: isFetching && !isLoading ? 0.9 : 1 }}>
        <div className="chart-title">Slot Summary <span>{isLoading ? 'Loading...' : `${dateLabel(date)}${data?.redis ? ` - Redis ${data.redis}` : ''}`}</span></div>
        {error ? <div className="empty-state"><p>{error.message || 'Slot Summary failed to load.'}</p></div> : null}
        <div className="slot-insight-grid">
          <InsightCard label="Day Reservations" value={metric(data?.totals?.count)} sub={`${metric(data?.totals?.pax)} reserved PAX - ${metric(data?.totals?.billing)} billing PAX`} color="var(--green)" />
          <InsightCard label="Top Slot" value={topSlot?.key || 'No slot data'} sub={topSlot ? `${metric(topSlot.count)} reservations - ${metric(topSlot.pax)} reserved PAX` : 'No bookings for this date.'} color="var(--blue)" />
          <InsightCard label="Top Branch" value={topBranch?.key || 'No branch data'} sub={topBranch ? `${metric(topBranch.pax)} reserved PAX - ${metric(topBranch.billing)} billing PAX` : 'No branch activity for this date.'} color="var(--orange)" />
          <InsightCard label="Same Weekday" value={`${sameWeekdayDiff > 0 ? '+' : ''}${metric(sameWeekdayDiff)} PAX`} sub={`Versus ${dateLabel(previousWeek(date))} - ${reservationDiff > 0 ? '+' : ''}${metric(reservationDiff)} reservations`} color="var(--purple)" />
        </div>
        <div className="chart-card slot-summary-inner-card">
          <div className="chart-title">Day Slot Chart <span>{dateLabel(date)}</span></div>
          <SlotTimeline branchRows={branchRows} segments={segments} date={date} canManageLocks={false} onManageLock={setLockTarget} />
        </div>
        <BranchTables branchRows={branchRows} />
      </div>
      {lockTarget ? <SlotLockModal item={lockTarget} date={date} onClose={() => setLockTarget(null)} onSaved={() => queryClient.invalidateQueries({ queryKey: queryKeys.slotSummary(params) })} /> : null}
    </>
  );
}

export function SlotSummaryPageMount() {
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => setTarget(document.getElementById('react-slot-summary')), []);
  if (!target || !isFeatureEnabled('USE_REACT_SLOT_SUMMARY')) return null;
  return createPortal(<SlotSummaryPage />, target);
}

export function SlotManagementPage() {
  const [date, setDate] = React.useState(todayKey());
  const [branch, setBranch] = React.useState('');
  const [lockTarget, setLockTarget] = React.useState(null);
  const [tab, setTab] = React.useState('manage');
  const [historyPage, setHistoryPage] = React.useState(1);
  const [historyDraft, setHistoryDraft] = React.useState({ start: relativeDateKey(-2), end: todayKey(), action: '' });
  const [historyApplied, setHistoryApplied] = React.useState({ start: relativeDateKey(-2), end: todayKey(), action: '' });
  const queryClient = useQueryClient();
  const managementUser = isManagementUser();
  const canAddReservation = managementUser || window.canCreateReservations?.() === true;
  const visible = visibleBranches();
  const branches = branch ? [branch] : BRANCHES.filter(item => visible.includes(item));
  const params = React.useMemo(() => buildParams(date, branch), [date, branch]);
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: queryKeys.slotSummary(params),
    queryFn: () => getSlotSummary(params),
    placeholderData: previous => previous
  });
  const branchRows = React.useMemo(
    () => branches.map(item => ({ branch: item, color: BRANCH_COLORS[item] || 'var(--accent)', rows: branchSlotRows(data, item, date) })),
    [branches.join('|'), data, date]
  );
  const historyQuery = useQuery({
    queryKey: ['slot-lock-history', historyPage, branch, historyApplied],
    queryFn: () => getSlotLockHistory({ page: historyPage, limit: 50, branch, ...historyApplied }),
    enabled: tab === 'history',
    placeholderData: previous => previous
  });
  const applyHistoryFilters = () => {
    setHistoryApplied({ ...historyDraft });
    setHistoryPage(1);
  };

  return (
    <>
      <div className="page-header">
        <h1>Slot Management</h1>
        <p>Review slot activity and control reservation availability.</p>
        {canAddReservation ? <button type="button" className="btn-primary add-reservation-glow" style={{ marginTop: 12 }} onClick={() => window.openReservationModal?.()}>Add Reservation</button> : null}
      </div>
      <div className="filters-bar slot-management-page-filters">
        <div className="filter-group"><span className="filter-label">Branch</span><select value={branch} onChange={event => { setBranch(event.target.value); setHistoryPage(1); }}><option value="">All</option>{visible.map(item => <option key={item} value={item}>{item}</option>)}</select></div>
        <div className="filter-group"><span className="filter-label">Management Date</span><input type="date" min={todayKey()} value={date} onChange={event => setDate(event.target.value || todayKey())} /></div>
        <div className="segmented-control slot-management-tabs">
          <button type="button" className={tab === 'manage' ? 'active' : ''} onClick={() => setTab('manage')}>Slot Controls</button>
          <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>Management History</button>
        </div>
      </div>
      {tab === 'manage' ? (
        <div className="slot-management-page-card" style={{ opacity: isFetching && !isLoading ? 0.9 : 1 }}>
          {error ? <div className="empty-state"><p>{error.message || 'Slot Management failed to load.'}</p></div> : null}
          <SlotManagement branchRows={branchRows} date={date} canManageLocks={managementUser} onManageLock={setLockTarget} />
        </div>
      ) : (
        <div className="chart-card slot-management-history">
          <div className="chart-title">Slot Management History <span>{Number(historyQuery.data?.total || 0).toLocaleString()} actions</span></div>
          <div className="filters-bar slot-management-history-filters">
            <div className="filter-group"><span className="filter-label">From</span><input type="date" value={historyDraft.start} onChange={event => setHistoryDraft(value => ({ ...value, start: event.target.value }))} /></div>
            <div className="filter-group"><span className="filter-label">To</span><input type="date" value={historyDraft.end} onChange={event => setHistoryDraft(value => ({ ...value, end: event.target.value }))} /></div>
            <div className="filter-group"><span className="filter-label">Event</span><select value={historyDraft.action} onChange={event => setHistoryDraft(value => ({ ...value, action: event.target.value }))}>
              <option value="">All Events</option>
              <option value="locked">Locked</option>
              <option value="unlocked">Unlocked</option>
            </select></div>
            <button type="button" className="btn-primary reports-apply" onClick={applyHistoryFilters}>Apply</button>
          </div>
          {historyQuery.isLoading ? <div className="empty-state"><p>Loading slot history...</p></div> : null}
          {historyQuery.error ? <div className="empty-state"><p>{historyQuery.error.message}</p></div> : null}
          {!historyQuery.isLoading && !historyQuery.data?.logs?.length ? <div className="empty-state"><p>No slot-management activity found.</p></div> : null}
          <div className="reservation-history-list">
            {(historyQuery.data?.logs || []).map(log => (
              <div key={log.id} className="reservation-history-entry">
                <div className="reservation-history-entry-head">
                  <strong>{String(log.action || '').toUpperCase()}</strong>
                  <span>{pkDateTimeLabel(log.performedAt)} PKT</span>
                </div>
                <div className="slot-history-route"><strong>{log.branch}</strong><span>{slotLabel(log.slot)}</span><span>{dateLabel(log.bookingDate)}</span></div>
                <div className="reservation-history-change"><span>State</span><strong>{log.previousValue || 'Open'} → {log.newValue || 'Locked'}</strong></div>
                <p>By {log.performedBy || 'Unknown User'}</p>
                {log.remarks ? <p>Remarks: {log.remarks}</p> : null}
              </div>
            ))}
          </div>
          <div className="pagination">
            <span className="page-info">Page {historyQuery.data?.page || 1} of {historyQuery.data?.totalPages || 1}</span>
            <button className="page-btn" type="button" disabled={historyPage <= 1 || historyQuery.isFetching} onClick={() => setHistoryPage(value => Math.max(1, value - 1))}>Previous</button>
            <button className="page-btn" type="button" disabled={historyPage >= Number(historyQuery.data?.totalPages || 1) || historyQuery.isFetching} onClick={() => setHistoryPage(value => value + 1)}>Next</button>
          </div>
        </div>
      )}
      {lockTarget ? <SlotLockModal item={lockTarget} date={date} onClose={() => setLockTarget(null)} onSaved={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.slotSummary(params) });
        queryClient.invalidateQueries({ queryKey: ['slot-lock-history'] });
      }} /> : null}
    </>
  );
}

export function SlotManagementPageMount() {
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => setTarget(document.getElementById('react-slot-management')), []);
  if (!target) return null;
  return createPortal(<SlotManagementPage />, target);
}
